import { useI18n } from 'vue-i18n';
import * as XLSX from 'xlsx-js-style';
import useToast from '@composables/useToast';
import { saveAs } from 'file-saver';
// @ts-ignore // no type definitions exist for the geojson2shp package
import { GeoJson2Shp } from '@gis-js/geojson2shp';
import JSZip from 'jszip';
// @ts-ignore // no type definitions exist for the html2pdf.js package
import html2pdf from 'html2pdf.js';
import html2canvas from 'html2canvas';
import type { FeatureCollection } from 'geojson';

export type XlsxCellStyle = {
  border: { top: { style: string }; bottom: { style: string }; left: { style: string }; right: { style: string } };
};
export type XlsxCell = { v: string | number; t: string; s: XlsxCellStyle };
export type CsvCell = { [key: string]: string | number };
export type MergeCellsRange = { s: { r: number; c: number }; e: { r: number; c: number } };
export type ColWidth = { wch: number };
export type FeaturesData = FeatureCollection & { crs: { type: string; properties: { name: string } } }; // GeoJSON standard no longer supports explicit specification of crs, so this is custom type to maintain compatibility

export default function useExport() {
  const { addToast } = useToast();
  const { t } = useI18n();
  const parseDate = (date: TmDate) => date?.replaceAll('. ', '.');

  const generateCsvContent = (data: CsvCell[], colKeys?: string[]): string => {
    const csvRows = [];
    const headers = colKeys || Object.keys(data[0]);
    csvRows.push(headers.map((h) => `"${h}"`).join(','));
    for (const item of data) {
      const values = headers.map((h) => `"${item[h] ?? ''}"`);
      csvRows.push(values.join(','));
    }
    return csvRows.join('\n');
  };

  const generateXlsxContent = (
    data: XlsxCell[][],
    mergeCellsRanges: MergeCellsRange[] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 0 } }],
    colWidth: ColWidth[] = [],
  ): string => {
    const ws = XLSX.utils.aoa_to_sheet(data);
    // Add the merge to the worksheet
    ws['!merges'] = mergeCellsRanges;
    // Add col width
    ws['!cols'] = colWidth;
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
    return XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  };

  const downloadAsCsv = (dataAsCsv: CsvCell[], exportName: string, colKeys?: string[]) => {
    // datatable.value.exportCSV(); // CSV export can also be done using the Prime datatable method
    if (!dataAsCsv.length)
      return addToast({
        severity: 'error',
        summary: t('export.export canceled'),
        detail: t('export.no data for export'),
      });
    const csvContent = generateCsvContent(dataAsCsv, colKeys);
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8' });
    saveAs(blob, `${exportName}.csv`);
  };

  const downloadAsXlsx = ({
    xlsxData,
    exportDate,
    exportName,
    mergeCellsRanges,
    colWidth,
  }: {
    xlsxData: XlsxCell[][];
    exportDate: string;
    exportName: string;
    mergeCellsRanges?: MergeCellsRange[];
    colWidth?: ColWidth[];
  }) => {
    const fileName = `${exportName} - ${exportDate}.xlsx`;
    if (!xlsxData.length)
      return addToast({
        severity: 'error',
        summary: t('export.export canceled'),
        detail: t('export.no data for export'),
      });
    const xlsxContent = generateXlsxContent(xlsxData, mergeCellsRanges, colWidth);
    const blob = new Blob([xlsxContent], { type: 'application/octet-stream' });
    saveAs(blob, fileName);
  };

  const downloadFeaturesAsGeojson = async (data: FeaturesData, outputFileName: string) => {
    const json = JSON.stringify(data);
    const blob = new Blob([json], { type: 'application/geo+json' });
    saveAs(blob, `${outputFileName}.geojson`);
  };

  const downloadFeaturesAsShp = async (data: FeaturesData, outputFileName: string) => {
    const cpg = 'UTF-8';
    // NOTE: projection string was generated by bot, it should correspond with the original geojson in EPSG:3857
    const projection =
      'PROJCS["WGS 84 / Pseudo-Mercator", GEOGCS["WGS 84", DATUM["WGS_1984", SPHEROID["WGS 84",6378137,298.257223563, AUTHORITY["EPSG","7030"]], AUTHORITY["EPSG","6326"]], PRIMEM["Greenwich",0, AUTHORITY["EPSG","8901"]], UNIT["degree",0.0174532925199433, AUTHORITY["EPSG","9122"]], AUTHORITY["EPSG","4326"]], PROJECTION["Mercator_2SP"], PARAMETER["standard_parallel_1",0], PARAMETER["central_meridian",0], PARAMETER["false_easting",0], PARAMETER["false_northing",0], UNIT["metre",1, AUTHORITY["EPSG","9001"]], AXIS["Easting",EAST], AXIS["Northing",NORTH], AUTHORITY["EPSG","3857"]]';
    const zip = new JSZip();
    const layers = zip.folder('layers');
    const g2s = new GeoJson2Shp(data);
    if (!layers) return addToast({ severity: 'error', summary: t('export.export canceled') });
    const writeToFile = (fileName: string) => (err: any, files: any) => {
      layers.file(`${fileName}.shp`, files.shp.buffer, { binary: true });
      layers.file(`${fileName}.shx`, files.shx.buffer, { binary: true });
      layers.file(`${fileName}.dbf`, files.dbf.buffer, { binary: true });
      layers.file(`${fileName}.prj`, projection);
      layers.file(`${fileName}.cpg`, cpg);
    };
    await g2s.writePoint(writeToFile('points')); // write all features with point geometry if any
    await g2s.writePolyline(writeToFile('lines')); // write all features with line geometry if any
    const blob = await zip.generateAsync({ type: 'blob' });
    saveAs(blob, `${outputFileName}.zip`);
  };

  const downloadPublicFile = async (fileName: string) => {
    const baseUrl = import.meta.env.BASE_URL;
    const response = await fetch(`${baseUrl}files/${fileName}`);
    if (!response.ok) return;
    const blob = await response.blob();
    saveAs(blob, fileName);
  };

  const downloadSvgAsPdf = async (svgElement: HTMLElement) => {
    html2pdf(svgElement, {
      filename: 'crossroad.pdf',
      image: { type: 'png', quality: 0.95 },
      html2canvas: { scale: 1.2 }, // expected around 3 MB file size
      jsPDF: { unit: 'pt', format: 'a4', orientation: 'portrait' },
    });
  };

  const downloadSvgAsPng = async (svgElement: HTMLElement, isSvgPresentInDom = true) => {
    if (!isSvgPresentInDom) {
      svgElement.style.position = 'absolute'; // make element position absolute
      svgElement.style.left = '-9999px'; // and move element off-screen before appending it to document body
      document.body.appendChild(svgElement); // html2canvas needs the element to be present in the document (it creates iframe copy of the entire DOM)
    }
    const canvas = await html2canvas(svgElement, { scale: 1.2 });
    canvas.toBlob((blob) => blob && saveAs(blob, 'crossroad.png'));
    if (!isSvgPresentInDom) document.body.removeChild(svgElement); // remove the element after it has been exported
  };

  const downloadSvgAsSvg = (svgElement: HTMLElement) => {
    const svgString = new XMLSerializer().serializeToString(svgElement);
    const blob = new Blob([svgString], { type: 'image/svg+xml' });
    saveAs(blob, 'crossroad.svg');
  };

  const cloneSvgElementForDownload = (svgElement: HTMLElement, rotationAngle = 0) => {
    const pxWidth = Math.floor(595 / 0.75); // full A4 width in px
    const pxPadding = pxWidth / 10;
    const renderElement = document.createElement('div');
    renderElement.style.width = `${pxWidth}px`;
    renderElement.style.height = `${pxWidth}px`;
    renderElement.style.padding = `${pxPadding}px`;
    if (rotationAngle) renderElement.style.transform = `rotate(${rotationAngle}deg)`;
    const svgClone = svgElement.cloneNode(true) as HTMLElement;
    svgClone.setAttribute('height', `${pxWidth - 2 * pxPadding}px`);
    svgClone.setAttribute('width', `${pxWidth - 2 * pxPadding}px`);
    renderElement.appendChild(svgClone);
    return renderElement;
  };

  return {
    generateCsvContent,
    generateXlsxContent,
    downloadAsCsv,
    downloadAsXlsx,
    parseDate,
    downloadFeaturesAsGeojson,
    downloadFeaturesAsShp,
    downloadPublicFile,
    downloadSvgAsPdf,
    downloadSvgAsPng,
    downloadSvgAsSvg,
    cloneSvgElementForDownload,
  };
}
